import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { RELAY_URL, RELAY_AES_KEY } from '../../config/config';

const DEVICE_ID_KEY = '_lab4u_did';
const INITIAL_REFERRER_KEY = '_lab4u_iref';

interface ClientProperties {
  time: number;
  current_url: string;
  referrer: string;
  initial_referrer: string;
  screen_height: number;
  screen_width: number;
  device_id: string;
}

interface TrackerPayload {
  event_name: string;
  properties: {
    distinct_id: string;
    $insert_id: string;
    [key: string]: any;
  };
}

interface ProfilerPayload {
  id: string;
  email: string;
  usertype?: string;
  first_name?: string;
  last_name?: string;
  usergroup?: string;
}

type RelayAction = 'TRACKER' | 'PROFILER';

@Injectable({ providedIn: 'root' })
export class TelemetryRelayService {
  /** Cached after identify() — used as distinct_id in every subsequent track() */
  private currentDistinctId = '';
  /** Cached after identify() —  */
  private jwtKey: 'token' | 'lti-jwt' = 'token';

  constructor(private http: HttpClient) {}

  //$ ---- PUBLIC API ----

  /** Send a PROFILER action to the Mixpanel Relay which identify the User with people.set()
   * and also store the distinct id on memory for later use
   * @param id Id of the User
   * @param email Email of the User
   * @param firstName First Name of the User (Optional)
   * @param lastName Last Name of the User (Optional)
   * @param userGroup Usergroup of the User (Optional)
   * @param userType User Type of the User (Optional)
   */
  identify(
    is_lti: boolean,
    id: string,
    email: string,
    firstName?: string,
    lastName?: string,
    userGroup?: string,
    userType?: string,
  ): void {
    this.currentDistinctId = email.toUpperCase();
    this.jwtKey = is_lti ? 'lti-jwt' : 'token';

    const payload: ProfilerPayload = {
      id,
      email: email.toUpperCase(),
      ...(firstName && { first_name: firstName.toUpperCase() }),
      ...(lastName && { last_name: lastName.toUpperCase() }),
      ...(userGroup && { usergroup: userGroup.toUpperCase() }),
      ...(userType && { usertype: userType.toUpperCase() }),
    };

    console.log(`[TELEMETRY] Calling Relay with PROFILER`);
    this.dispatch('PROFILER', payload);
  }

  /** Send a TRACKER action to the Mixpanel Relay
   * @param eventName Name of the Event
   * @param properties Custom Properties for the Event
   */
  track(eventName: string, properties: Record<string, any> = {}): void {
    const payload: TrackerPayload = {
      event_name: eventName,
      properties: {
        distinct_id: this.currentDistinctId,
        $insert_id: crypto.randomUUID(),
        ...properties,
      },
    };

    console.log(`[TELEMETRY] Calling Relay with TRACKER`);
    this.dispatch('TRACKER', payload);
  }

  reset(): void {
    // device_id intentionally survives reset (persistent device fingerprint)
    // only wipe the in-memory session identity
    console.log(`[TELEMETRY] Resetting Relay Distinct Id`);
    this.currentDistinctId = '';
  }

  //$ ---- UTILITIES ----

  /** Get the current LTI for the session having LTI as priority
   * @returns The current JWT of the logged in user
   */
  private getJwt(): string | null {
    // LTI sessions take priority
    const token = localStorage.getItem(this.jwtKey);
    console.log(`[TELEMETRY] JWT key being used: ${this.jwtKey}`);
    return token;
  }

  /** Unique device Id for the specific device being Used. It used to be managed by
   * Mixpanel SDK but now its handled locally with a value on localStorage
   * @returns The unique device id
   */
  private getOrCreateDeviceId(): string {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  }

  /** Tries to get the initial referer from a localStorage value. If the value its empty,
   * then it initializes it
   * @returns The Initial Referer as a string
   */
  private getOrSetInitialReferrer(): string {
    let ref = localStorage.getItem(INITIAL_REFERRER_KEY);
    if (!ref) {
      ref = document.referrer || '$direct';
      localStorage.setItem(INITIAL_REFERRER_KEY, ref);
    }
    return ref;
  }

  /** Build the object with all properties needed from the client side and the Function App can't read
   * @returns The Client Properties ready to send to the Relay
   */
  private buildClientProperties(): ClientProperties {
    return {
      time: Date.now(),
      current_url: window.location.href,
      referrer: document.referrer || '$direct',
      initial_referrer: this.getOrSetInitialReferrer(),
      screen_height: window.screen.height,
      screen_width: window.screen.width,
      device_id: this.getOrCreateDeviceId(),
    };
  }

  /** Encrypt the Payload by aes-256-cbc encryption via Web Crypto API
   * Output format: ivHex:ciphertextHex
   */
  private async encryptPayload(body: object): Promise<string> {
    const encoder = new TextEncoder();

    // Key is used as raw bytes directly, no hashing
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      encoder.encode(RELAY_AES_KEY), // 32 ASCII chars = 32 bytes = AES-256
      { name: 'AES-CBC' },
      false,
      ['encrypt'],
    );

    const iv = crypto.getRandomValues(new Uint8Array(16));

    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-CBC', iv },
      cryptoKey,
      encoder.encode(JSON.stringify(body)),
    );

    const toHex = (buf: ArrayBuffer | Uint8Array) =>
      Array.from(new Uint8Array(buf instanceof ArrayBuffer ? buf : buf.buffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');

    return `${toHex(iv)}:${toHex(ciphertext)}`;
  }

  /** Call the Relay API
   * @param action Action request for the Relay
   * @param payload Payload of the request, depends on the Action requested
   */
  private dispatch(
    action: RelayAction,
    payload: TrackerPayload | ProfilerPayload,
  ): void {
    const jwt = this.getJwt();
    if (!jwt) {
      console.warn('[TELEMETRY] No JWT found in localStorage — EVENT DROPPED');
      return;
    }

    const body = {
      action,
      client_properties: this.buildClientProperties(),
      payload,
    };

    this.encryptPayload(body)
      .then((encrypted) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        });

        // fire-and-forget — telemetry must never affect app behavior
        this.http
          .post(
            RELAY_URL,
            { data: encrypted },
            { headers, responseType: 'text' },
          )
          .subscribe({
            error: (err) =>
              console.warn('[TELEMETRY] Relay error:', err.status),
          });
      })
      .catch((err) => console.warn('[TELEMETRY] Encryption failed:', err));
  }
}
